/* istanbul ignore file */
import { FormSection, FormRow, MediumFormRow, BigFormRow, FormInputGroup } from 'framework/ui/layout';
import { CSSProperties } from 'react';
import {
  CurrencyInput,
  DateInput,
  SelectInput,
  TextArea,
  TextInput,
  ClearAfterUnmountWrapper,
  Option as OptionType,
  BooleanDropdownOptionInput,
  BOOLEAN_VALUE,
} from 'framework/form';
import RelatedDiscoveryOpportunityInputDropdown from 'domain/sales/opportunity/shared/input/RelatedDiscoveryOpportunityInputDropdown';
import OpportunityChecklist from 'domain/sales/shared/checklist/OpportunityChecklist';
import { ACCOUNT_TYPE } from '../../account/shared/input/AccountInput';
import { CLOSED_LOST, CLOSED_WON, POSTPONED } from 'domain/sales/opportunity/opportunityService';
import AccountInput from 'domain/sales/account/shared/input/AccountInput';
import OpportunityTypeDropdown from 'domain/sales/opportunity/shared/input/OpportunityTypeDropdown';
import OpportunityStageInput from 'domain/sales/opportunity/shared/input/OpportunityStageInput';
import OpportunityOwnerInput from 'domain/sales/opportunity/shared/input/OpportunityOwnerInput';
import ClosedReasonDropdown from 'domain/sales/opportunity/shared/input/ClosedReasonDropdown';
import { ARTIFACT_ID } from 'domain/shared/artifact';
import CompositeDocumentUploaderInput from 'domain/sales/opportunity/shared/storage/components/CompositeDocumentUploaderInput';
import { ProjectsByAccountDropdown } from 'domain/shared/newProject';
import PresaleCoordinatorInput from 'domain/sales/opportunity/presale/output/shared/input/PresaleCoordinatorInput';
import { useAuthorization } from 'framework/authorization';
import { CHANGE_PRESALE_COORDINATOR } from 'domain/sales/salesService';

interface OpportunityFormProps {
  isCreateForm?: boolean;
}

const ratesOptions: OptionType[] = [
  { value: 'by', label: 'BY' },
  { value: 'eu', label: 'EU' },
];

/**
 * Transform form data before submission
 * - For isContractRenewal=NO, combine checklist attachments with main attachments
 */
export const transformFormData = (data: any): any => {
  if (!data) return data;

  const transformedData = { ...data };

  transformedData.isContractRenewal = data.isContractRenewal === '' ? undefined : data.isContractRenewal;

  if (transformedData.isContractRenewal === BOOLEAN_VALUE.YES) {
    const { relatedProject } = data;

    if (relatedProject !== null && relatedProject !== undefined && relatedProject !== '') {
      const projectId = typeof relatedProject === 'object' ? relatedProject.id : relatedProject;
      const numericId = Number(projectId);

      if (!isNaN(numericId) && Number.isInteger(numericId)) {
        transformedData.relatedProject = numericId;
      } else {
        transformedData.relatedProject = null;
      }
    } else {
      transformedData.relatedProject = null;
    }
  }

  transformedData.riskScore = data.riskScore ? Number(data.riskScore) : null;

  if (transformedData.isContractRenewal === BOOLEAN_VALUE.NO) {
    const inputMaterials = transformedData.opportunityChecklist?.inputMaterials || [];
    const firstCallRecording = transformedData.opportunityChecklist?.firstCallRecording || [];
    const currentAttachments = transformedData.attachmentsIds || [];

    transformedData.attachmentsIds = [...new Set([...currentAttachments, ...inputMaterials, ...firstCallRecording])];
  }

  const limitFields = ['upperLimit', 'mostExpected', 'lowerLimit', 'rates'];
  limitFields.forEach(field => {
    if (data[field] !== undefined) {
      transformedData.opportunityChecklist = transformedData.opportunityChecklist || {};
      transformedData.opportunityChecklist[field] = data[field];
    }
  });

  return transformedData;
};

const customSxChild = {
  2: {
    flex: {
      xs: `0 0 calc(100%)`,
      sm: `0 0 calc(100%)`,
      md: `0 0 calc(100%)`,
      xl: `0 0 calc(50%)`,
    },
    maxWidth: {
      xs: `calc(100%)`,
      sm: `calc(100%)`,
      md: `calc(100%)`,
      xl: `calc(50%)`,
    },
  },
};

const formSectionStyle: CSSProperties = {
  marginBottom: '15px',
};

const OpportunityForm: React.FC<OpportunityFormProps> = ({ isCreateForm }) => {
  const { isGranted } = useAuthorization();
  const isClosedReasonInputsVisible = (opportunity: any) =>
    opportunity?.stateStep &&
    (opportunity?.stateStep?.toString() === CLOSED_WON.toString() ||
      opportunity?.stateStep?.toString() === CLOSED_LOST.toString());

  return (
    <>
      <FormSection formStyle={formSectionStyle}>
        <AccountInput source="" idSource="accountId" nameSource="accountName" contactSource="primaryContactId" />
      </FormSection>
      <FormSection formStyle={formSectionStyle}>
        <FormRow customSxChild={customSxChild}>
          <TextInput source="name" label="Title" isRequired />
          <OpportunityTypeDropdown source="type" isRequired />
          <OpportunityStageInput source="stateStep" disabled={isCreateForm} />
        </FormRow>

        <FormRow>
          <ClearAfterUnmountWrapper isVisible={opportunity => opportunity.stateStep !== POSTPONED}>
            <DateInput
              source="closeDate"
              label="Closing date"
              tooltip="The final date by when the customer will make a decision"
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={opportunity => opportunity.stateStep === POSTPONED}>
            <DateInput source="postponedUntil" />
          </ClearAfterUnmountWrapper>
          <BooleanDropdownOptionInput
            source="isContractRenewal"
            label="Is this a renewal of an existing contract?"
            placeholder=" "
          />
          <ClearAfterUnmountWrapper
            isVisible={data =>
              data?.isContractRenewal === BOOLEAN_VALUE.YES && data.accountType === ACCOUNT_TYPE.EXISTING
            }
          >
            <ProjectsByAccountDropdown
              source="relatedProject"
              label="Related project"
              accountAliasSource="accountId"
              isRequired
            />
          </ClearAfterUnmountWrapper>
        </FormRow>
        <FormRow>
          <OpportunityOwnerInput source="ownerId" />
          <PresaleCoordinatorInput
            source="presaleCoordinatorAlias"
            label="Presale coordinator"
            placeholder="Select..."
            isVisible={isGranted(CHANGE_PRESALE_COORDINATOR)}
          />
        </FormRow>

        <MediumFormRow>
          <ClearAfterUnmountWrapper isVisible={isClosedReasonInputsVisible}>
            <ClosedReasonDropdown
              type="PRIMARY"
              source="closedReason.primaryReason"
              stageSource="stateStep"
              label="Primary reason for closed status"
              placeholder="Choose Primary Closed Reason"
              tooltip="Select the primary reason of why the Opportunity has been Won (Lost)."
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={isClosedReasonInputsVisible}>
            <ClosedReasonDropdown
              type="SECONDARY"
              source="closedReason.secondaryReason"
              stageSource="stateStep"
              label="Secondary reason for closed status"
              placeholder="Choose Secondary Closed Reason"
              tooltip="Select the secondary reason of why the Opportunity has been Won (Lost)."
            />
          </ClearAfterUnmountWrapper>
        </MediumFormRow>
        <MediumFormRow>
          <ClearAfterUnmountWrapper isVisible={isClosedReasonInputsVisible}>
            <TextArea
              label="Closed summary"
              source="closedReason.summary"
              tooltip="Provide a summary of why the Opportunity was Won (Lost). Quote prospect's feedback, when available."
            />
          </ClearAfterUnmountWrapper>
        </MediumFormRow>
      </FormSection>

      <FormSection
        formStyle={formSectionStyle}
        isVisible={(data: any) =>
          Boolean(isCreateForm) &&
          data.isContractRenewal === BOOLEAN_VALUE.NO &&
          data.accountType === ACCOUNT_TYPE.EXISTING &&
          !!data.accountId
        }
      >
        <FormRow>
          <ClearAfterUnmountWrapper
            isVisible={data =>
              data.isContractRenewal === BOOLEAN_VALUE.NO &&
              data.accountType === ACCOUNT_TYPE.EXISTING &&
              !!data.accountId
            }
          >
            <BooleanDropdownOptionInput
              source="isAfterDiscovery"
              label="Is development after discovery"
              placeholder="Select..."
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={data => data.isAfterDiscovery === BOOLEAN_VALUE.YES}>
            <RelatedDiscoveryOpportunityInputDropdown
              accountAliasSource="accountId"
              source="discoveryOpportunityId"
              label="Discovery opportunity"
              errorSource="discoveryOpportunity"
            />
          </ClearAfterUnmountWrapper>
        </FormRow>
      </FormSection>

      <FormSection formStyle={formSectionStyle}>
        <FormRow>
          <SelectInput
            label="Rates"
            tooltip="What rates should be used for pricing (BY or EU)"
            source="rates"
            options={ratesOptions}
            isRequired
          />
        </FormRow>
        <FormRow>
          <CurrencyInput
            label="Maximum total cost"
            tooltip="The upper limit of the expected budget, i.e. we will definitely lose the opportunity if we go over this budget"
            source="upperLimit"
            isRequired
            suffix="$"
          />
          <CurrencyInput
            label="Most expected total cost"
            tooltip="Most likely available budget based on information provided by the customer or our best guess"
            source="mostExpected"
            isRequired
            suffix="$"
          />
          <CurrencyInput
            label="Minimum recommended cost"
            tooltip="The lower boundary of the suggested budget"
            source="lowerLimit"
            isRequired
            suffix="$"
          />
        </FormRow>
      </FormSection>

      <FormSection
        formStyle={formSectionStyle}
        isVisible={data => !isCreateForm || data?.isContractRenewal === BOOLEAN_VALUE.YES}
      >
        <BigFormRow>
          <CompositeDocumentUploaderInput
            label="Attachments"
            artifactTypeId={ARTIFACT_ID.CRM_ATTACHMENT}
            artifactsListSource={'attachmentsIds'}
            documentsListsSource={'artifactPaths'}
          />
        </BigFormRow>
      </FormSection>

      <FormInputGroup isVisible={data => data?.isContractRenewal === BOOLEAN_VALUE.NO && Boolean(isCreateForm)}>
        <OpportunityChecklist source="opportunityChecklist" hideAttachmentSections={false} />
      </FormInputGroup>

      <FormSection formStyle={formSectionStyle}>
        <MediumFormRow>
          <TextArea label="Description" source="description" rows={1} />
          <MediumFormRow>
            <TextInput source="nextStep" label="Next step" />
            <DateInput source="nextStepDeadline" label="Next step deadline" />
          </MediumFormRow>
        </MediumFormRow>
      </FormSection>
    </>
  );
};

export default OpportunityForm;
